import { Component, Input, OnInit, signal, viewChild } from '@angular/core';
import { WidgetBase } from '../../../models';
import { LaserWidgetPlaceholder } from './laser-widget-placeholder';
import { LaserWidgetPreview } from './laser-widget-preview';
import { LaserWidgetEdit } from './laser-widget-edit';
import { LaserTreatmentItem, LaserTreatmentMetadataItem } from './laser-treatment-item';
import { LaserTreatmentControlLabels } from './laser-treatment-control-labels';
import { LaserWidgetPrint } from './laser-widget-print';
import { parseLocalDateString } from '../../../helpers/date-utils';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

@Component({
  selector: 'fb-laser-widget',
  imports: [LaserWidgetPlaceholder, LaserWidgetPrint, LaserWidgetPreview, LaserWidgetEdit],
  templateUrl: './laser-widget.html',
  styleUrl: './laser-widget.scss',
})
export class LaserWidget extends WidgetBase implements OnInit {
  @Input() public isTemplatePrint = false;

  /**
   * Normalized payload for laser widget.
   */
  protected readonly data = signal<readonly LaserTreatmentItem[]>([]);

  /**
   * Appointment ID.
   */
  protected readonly appointmentIdValue = signal<string | undefined>(undefined);

  /**
   * Treatment ID.
   */
  protected readonly treatmentIdValue = signal<string | undefined>(undefined);

  /**
   * Patient remote ID.
   */
  protected readonly patientRemoteIdValue = signal<string | undefined>(undefined);

  /**
   * Whether the entity has `completed` status.
   */
  protected readonly isCompleted = signal(false);

  /**
   * Query for editable widget representation.
   */
  protected readonly edit = viewChild(LaserWidgetEdit);

  private appointmentValue: any;

  public override set appointment(value: any) {
    this.appointmentValue = value;

    this.setupAppointmentId();
  }
  public override get appointment() {
    return this.appointmentValue;
  }

  public ngOnInit(): void {
    this.setupData();
    this.setupAppointmentId();
    this.setupTreatmentId();
    this.setupPatientRemoteId();
    this.setupIsCompleted();

    // Signal readiness for form designer to avoid hanging state.
    this.ready.next(true);
  }

  /**
   * Returns metadata to be included in the request.
   */
  public getMetadata() {
    const edit = this.edit();

    if (!edit) {
      return {};
    }

    const metadata = edit.getMetadata();

    if (metadata === null) {
      return {};
    }

    const { treatmentId, records } = metadata;

    return {
      lasers: records.map((record) => ({
        id: record.id ?? undefined,
        date: parseLocalDateString(record.date),
        equipment_id: record.equipmentId ?? undefined,
        procedure_type_id: record.procedureTypeId ?? undefined,
        treatment_id: record.treatmentId ?? treatmentId ?? undefined,
        service_time: record.serviceTime ?? undefined,
        smart_cool: record.smartCool ?? undefined,
        joules: record.joules ?? undefined,
        pulse: record.pulse ?? undefined,
        rep_rate: record.repetitionRate ?? undefined,
        laser_option_1_id: record.firstLaserOptionId ?? undefined,
        laser_option_2_id: record.secondLaserOptionId ?? undefined,
        notes: record.note || undefined,
      })),
      treatment_id: treatmentId ?? undefined,
    };
  }

  public override isValid(): boolean {
    const edit = this.edit();

    if (!edit) {
      return true;
    }

    return edit.isValid();
  }

  public applySuggestionSet(patch: Record<string, unknown>): void {
    const edit = this.edit();

    if (!edit) {
      return;
    }

    edit.applySuggestionSet(patch);
  }

  public hasScribeDatasetContext(): boolean {
    return !!this.patientRemoteIdValue() && !!this.appointmentIdValue();
  }

  public areScribeOptionsLoaded(): boolean {
    return this.edit()?.areScribeOptionsLoaded() ?? false;
  }

  public getLaserEquipmentOptionsSnapshot(): Array<{ id: string; name: string }> {
    return this.edit()?.getLaserEquipmentOptionsSnapshot() ?? [];
  }

  public getLaserProcedureTypeOptionsSnapshot(): Array<{ id: string; name: string }> {
    return this.edit()?.getLaserProcedureTypeOptionsSnapshot() ?? [];
  }

  public getLaserOptionsByTypeSnapshot(): {
    first: Array<{ id: string; text: string }>;
    second: Array<{ id: string; text: string }>;
  } {
    return this.edit()?.getLaserOptionsByTypeSnapshot() ?? { first: [], second: [] };
  }

  /**
   * Updates the payload.
   */
  protected updatePayload(items: readonly LaserTreatmentItem[]): void {
    if (!this.payload['properties']) {
      return;
    }

    if (!this.payload.properties['items']) {
      this.payload.properties.items = { value: [] };
    }

    const currentItems = Array.isArray(this.payload.properties.items.value)
      ? this.payload.properties.items.value
      : [];

    this.payload.properties.items.formInvalid = !this.isValid();
    this.payload.properties.items.value = items.map((item, index) => {
      const result: ScribeSuggestionMarkerTarget = {
        id: item.id ?? undefined,
        treatment_id: item.treatmentId ?? undefined,
        date: parseLocalDateString(item.date),
        equipment_id: item.equipmentId,
        equipment_name: item.equipmentName ?? null,
        procedure_type_id: item.procedureTypeId,
        procedure_type_name: item.procedureTypeName ?? null,
        service_time: item.serviceTime,
        smart_cool: item.smartCool,
        joules: item.joules,
        pulse: item.pulse,
        rep_rate: item.repetitionRate,
        laser_option_1_id: item.firstLaserOptionId,
        laser_option_1_name: item.firstLaserOptionName ?? null,
        laser_option_2_id: item.secondLaserOptionId,
        laser_option_2_name: item.secondLaserOptionName ?? null,
        notes: item.note,
        labels: {
          smart_cool_text: item.labels.smartCoolText,
          joules_text: item.labels.joulesText,
          pulse_text: item.labels.pulseText,
          rep_rate_text: item.labels.repetitionRateText,
          laser_option_1_text: item.labels.firstLaserOptionText,
          laser_option_2_text: item.labels.secondLaserOptionText,
        },
      };
      const currentItem = currentItems[index];
      copyScribeSuggestionMarker(
        currentItem && typeof currentItem === 'object'
          ? (currentItem as ScribeSuggestionMarkerTarget)
          : (item as unknown as ScribeSuggestionMarkerTarget),
        result,
      );
      return result;
    });
  }

  protected getScribeMarkerTargets(): readonly (ScribeSuggestionMarkerTarget | null)[] {
    const items = this.payload?.properties?.items?.value;
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) =>
      item && typeof item === 'object' && !Array.isArray(item)
        ? (item as ScribeSuggestionMarkerTarget)
        : null,
    );
  }

  /**
   * Sets up the appointment ID.
   */
  private setupAppointmentId(): void {
    const appointmentId =
      this.appointment?.AppointmentId ??
      this.entity?.metadata?.appointment_id ??
      this.appointmentId;

    this.appointmentIdValue.set(appointmentId);
  }

  /**
   * Sets up the treatment ID.
   */
  private setupTreatmentId(): void {
    const treatmentId = this.entity?.metadata?.treatment_id;

    if (!this.isOptionalStringOrNumber(treatmentId)) {
      return;
    }

    this.treatmentIdValue.set(this.transformToNullableString(treatmentId) ?? undefined);
  }

  /**
   * Sets up the patient remote ID.
   */
  private setupPatientRemoteId(): void {
    this.patientRemoteIdValue.set(this.patient?.remote_id);
  }

  /**
   * Sets up the completed status.
   */
  private setupIsCompleted(): void {
    this.isCompleted.set(this.entity && this.entity['status'] === 5);
  }

  /**
   * Sets up the data for laser widget.
   */
  private setupData(): void {
    this.data.set(this.getData());
  }

  /**
   * Returns normalized laser widget data from the payload.
   */
  private getData(): readonly LaserTreatmentItem[] {
    const items = this.payload?.properties?.items?.value;

    if (!Array.isArray(items)) {
      return [];
    }

    const treatments = items.map((item) => this.parsePayloadItem(item)).filter(Boolean);

    return this.mergeTreatmentsWithMetadata(treatments);
  }

  /**
   * Fills missing treatment IDs from metadata records.
   */
  private mergeTreatmentsWithMetadata(
    treatments: readonly LaserTreatmentItem[],
  ): readonly LaserTreatmentItem[] {
    if (treatments.length === 0) {
      return treatments;
    }

    const metadataTreatments = this.getMetadataTreatments();

    if (metadataTreatments.length === 0) {
      return treatments;
    }

    const metadataIndex = new Map<string, string>();

    metadataTreatments.forEach((record) => {
      if (!record.id) {
        return;
      }

      const key = this.getTreatmentKey(record);

      if (!key) {
        return;
      }

      metadataIndex.set(key, record.id);
    });

    if (metadataIndex.size === 0) {
      return treatments;
    }

    return treatments.map((treatment) => {
      if (treatment.id) {
        return treatment;
      }

      const key = this.getTreatmentKey(treatment);
      const id = metadataIndex.get(key);

      return id ? { ...treatment, id } : treatment;
    });
  }

  /**
   * Normalizes metadata laser records for ID matching.
   */
  private getMetadataTreatments() {
    const value = this.entity?.metadata?.lasers;

    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => this.parseMetadataItem(item)).filter((record) => record !== null);
  }

  /**
   * Maps payload item to a laser item.
   */
  private parsePayloadItem(item: unknown): LaserTreatmentItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;

    const id = record['id'];
    const treatmentId = record['treatment_id'];
    const date = record['date'];
    const equipmentId = record['equipment_id'];
    const equipmentName = record['equipment_name'];
    const procedureTypeId = record['procedure_type_id'];
    const procedureTypeName = record['procedure_type_name'];
    const serviceTime = record['service_time'];
    const smartCool = record['smart_cool'];
    const joules = record['joules'];
    const pulse = record['pulse'];
    const repetitionRate = record['rep_rate'];
    const firstLaserOptionId = record['laser_option_1_id'];
    const firstLaserOptionName = record['laser_option_1_name'];
    const secondLaserOptionId = record['laser_option_2_id'];
    const secondLaserOptionName = record['laser_option_2_name'];
    const note = record['notes'];
    const labels = record['labels'];

    if (!this.isOptionalString(id)) {
      return null;
    }

    if (!this.isOptionalString(treatmentId)) {
      return null;
    }

    if (typeof date !== 'string') {
      return null;
    }

    const parsedDate = this.parseDate(date);

    if (parsedDate === null) {
      return null;
    }

    if (!this.isNullableString(equipmentId)) {
      return null;
    }

    if (!this.isOptionalString(equipmentName) && !this.isNullableString(equipmentName)) {
      return null;
    }

    const parsedEquipmentName = this.normalizeOptionalNullableString(equipmentName);

    if (!this.isNullableString(procedureTypeId)) {
      return null;
    }

    if (!this.isOptionalString(procedureTypeName) && !this.isNullableString(procedureTypeName)) {
      return null;
    }

    const parsedProcedureTypeName = this.normalizeOptionalNullableString(procedureTypeName);

    if (!this.isNullableNumber(serviceTime)) {
      return null;
    }

    if (!this.isNullableNumber(smartCool)) {
      return null;
    }

    if (!this.isNullableNumber(joules)) {
      return null;
    }

    if (!this.isNullableNumber(pulse)) {
      return null;
    }

    if (!this.isNullableNumber(repetitionRate)) {
      return null;
    }

    if (!this.isNullableString(firstLaserOptionId)) {
      return null;
    }

    if (
      !this.isOptionalString(firstLaserOptionName) &&
      !this.isNullableString(firstLaserOptionName)
    ) {
      return null;
    }

    const parsedFirstLaserOptionName = this.normalizeOptionalNullableString(firstLaserOptionName);

    if (!this.isNullableString(secondLaserOptionId)) {
      return null;
    }

    if (
      !this.isOptionalString(secondLaserOptionName) &&
      !this.isNullableString(secondLaserOptionName)
    ) {
      return null;
    }

    const parsedSecondLaserOptionName = this.normalizeOptionalNullableString(secondLaserOptionName);

    if (typeof note !== 'string') {
      return null;
    }

    const parsedLabels = this.parsePayloadItemLabels(labels);

    if (parsedLabels === null) {
      return null;
    }

    const result: LaserTreatmentItem = {
      id: id ?? null,
      treatmentId: treatmentId ?? null,
      date: parsedDate,
      equipmentId: equipmentId,
      equipmentName: parsedEquipmentName,
      procedureTypeId: procedureTypeId,
      procedureTypeName: parsedProcedureTypeName,
      serviceTime: serviceTime,
      smartCool: smartCool,
      joules: joules,
      pulse: pulse,
      repetitionRate: repetitionRate,
      firstLaserOptionId: firstLaserOptionId,
      firstLaserOptionName: parsedFirstLaserOptionName,
      secondLaserOptionId: secondLaserOptionId,
      secondLaserOptionName: parsedSecondLaserOptionName,
      note: note,
      labels: parsedLabels,
    };
    copyScribeSuggestionMarker(record, result as unknown as ScribeSuggestionMarkerTarget);
    return result;
  }

  /**
   * Parses the labels from the payload item.
   */
  private parsePayloadItemLabels(value: unknown): LaserTreatmentControlLabels | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;

    const smartCoolText = record['smart_cool_text'];
    const joulesText = record['joules_text'];
    const pulseText = record['pulse_text'];
    const repetitionRateText = record['rep_rate_text'];
    const firstLaserOptionText = record['laser_option_1_text'];
    const secondLaserOptionText = record['laser_option_2_text'];

    if (!this.isNullableString(smartCoolText)) {
      return null;
    }

    if (!this.isNullableString(joulesText)) {
      return null;
    }

    if (!this.isNullableString(pulseText)) {
      return null;
    }

    if (!this.isNullableString(repetitionRateText)) {
      return null;
    }

    if (!this.isNullableString(firstLaserOptionText)) {
      return null;
    }

    if (!this.isNullableString(secondLaserOptionText)) {
      return null;
    }

    return {
      smartCoolText,
      joulesText,
      pulseText,
      repetitionRateText,
      firstLaserOptionText,
      secondLaserOptionText,
    };
  }

  /**
   * Maps payload item to a laser item.
   */
  private parseMetadataItem(item: unknown): LaserTreatmentMetadataItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;

    const id = record['id'];
    const treatmentId = record['treatment_id'];
    const date = record['date'];
    const equipmentId = record['equipment_id'];
    const procedureTypeId = record['procedure_type_id'];
    const serviceTime = record['service_time'];
    const smartCool = record['smart_cool'];
    const joules = record['joules'];
    const pulse = record['pulse'];
    const repetitionRate = record['rep_rate'];
    const firstLaserOptionId = record['laser_option_1_id'];
    const secondLaserOptionId = record['laser_option_2_id'];
    const note = record['notes'];

    if (!this.isOptionalStringOrNumber(id)) {
      return null;
    }

    const parsedId = this.transformToNullableString(id);

    if (!this.isOptionalStringOrNumber(treatmentId)) {
      return null;
    }

    const parsedTreatmentId = this.transformToNullableString(id);

    if (typeof date !== 'string') {
      return null;
    }

    const parsedDate = this.parseDate(date);

    if (parsedDate === null) {
      return null;
    }

    if (!this.isOptionalStringOrNumber(equipmentId)) {
      return null;
    }

    const parsedEquipmentId = this.transformToNullableString(equipmentId);

    if (!this.isOptionalStringOrNumber(procedureTypeId)) {
      return null;
    }

    const parsedProcedureTypeId = this.transformToNullableString(procedureTypeId);

    if (!this.isOptionalNumber(serviceTime)) {
      return null;
    }

    const parsedServiceTime = serviceTime ?? null;

    if (!this.isOptionalNumber(smartCool)) {
      return null;
    }

    const parsedSmartCool = smartCool ?? null;

    if (!this.isOptionalNumber(joules)) {
      return null;
    }

    const parsedJoules = joules ?? null;

    if (!this.isOptionalNumber(pulse)) {
      return null;
    }

    const parsedPulse = pulse ?? null;

    if (!this.isOptionalNumber(repetitionRate)) {
      return null;
    }

    const parsedRepetitionRate = repetitionRate ?? null;

    if (!this.isOptionalStringOrNumber(firstLaserOptionId)) {
      return null;
    }

    const parsedFirstLaserOptionId = this.transformToNullableString(firstLaserOptionId);

    if (!this.isOptionalStringOrNumber(secondLaserOptionId)) {
      return null;
    }

    const parsedSecondLaserOptionId = this.transformToNullableString(secondLaserOptionId);

    if (!this.isOptionalString(note)) {
      return null;
    }

    const parsedNote = note ?? '';

    return {
      id: parsedId,
      treatmentId: parsedTreatmentId,
      date: parsedDate,
      equipmentId: parsedEquipmentId,
      procedureTypeId: parsedProcedureTypeId,
      serviceTime: parsedServiceTime,
      smartCool: parsedSmartCool,
      joules: parsedJoules,
      pulse: parsedPulse,
      repetitionRate: parsedRepetitionRate,
      firstLaserOptionId: parsedFirstLaserOptionId,
      secondLaserOptionId: parsedSecondLaserOptionId,
      note: parsedNote,
    };
  }

  /**
   * Builds a stable key used for metadata matching.
   */
  private getTreatmentKey(value: LaserTreatmentMetadataItem): string {
    return [
      parseLocalDateString(value.date),
      value.treatmentId,
      value.equipmentId,
      value.procedureTypeId,
      value.serviceTime,
      value.smartCool,
      value.joules,
      value.pulse,
      value.repetitionRate,
      value.firstLaserOptionId,
      value.secondLaserOptionId,
      value.note,
    ].join('|');
  }

  /**
   * Parses the date string and returns a Date object.
   */
  private parseDate(value: string): Date | null {
    const parsed = new Date(value);

    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Checks whether the given value is a nullable string.
   */
  private isNullableString(value: unknown): value is string | null {
    return value === null || typeof value === 'string';
  }

  /**
   * Checks whether the given value is a nullable number.
   */
  private isNullableNumber(value: unknown): value is number | null {
    return value === null || typeof value === 'number';
  }

  /**
   * Checks whether the given value is an optional string.
   */
  private isOptionalString(value: unknown): value is string | undefined {
    return value === undefined || typeof value === 'string';
  }

  /**
   * Checks whether the given value is an optional number.
   */
  private isOptionalNumber(value: unknown): value is number | undefined {
    return value === undefined || typeof value === 'number';
  }

  /**
   * Checks whether the given value is an optional string.
   */
  private isOptionalStringOrNumber(value: unknown): value is string | number | undefined {
    return value === undefined || typeof value === 'string' || typeof value === 'number';
  }

  /**
   * Normalizes optional nullable string payload value to `string | null`.
   */
  private normalizeOptionalNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  /**
   * Transforms the given value to a nullable string.
   */
  private transformToNullableString(value: string | number | undefined): string | null {
    if (value === undefined) {
      return null;
    }

    return `${value}`;
  }
}
